import React, {
  Dispatch,
  SetStateAction,
  PropsWithChildren,
  useMemo,
  ReactNode,
  CSSProperties,
} from "react";
import { Popover, ArrowContainer, PopoverPosition } from "react-tiny-popover";

import "./CustomPopover.scss";
import StepIndicator from "../StepIndicator/StepIndicator";
import Overlay from "../Overlay/Overlay";

interface CustomPopoverProps {
  isOpen: boolean;
  activeExplainer?: number;
  setActiveExplainer: Dispatch<SetStateAction<number | undefined>>;
  explainerSteps: number;
  popoverContent: ReactNode;
  heading?: ReactNode;
  positions?: PopoverPosition | PopoverPosition[];
  onFlowEnd?: () => void;
  childrenContainerStyles?: CSSProperties;
}

interface PopoverContentProps extends Omit<CustomPopoverProps, "isOpen"> {}

function PopoverContent({
  activeExplainer,
  setActiveExplainer,
  popoverContent,
  heading,
  explainerSteps,
  onFlowEnd,
}: PopoverContentProps) {
  const handlePrevious = () =>
    setActiveExplainer((prev) => (prev ? prev - 1 : prev));

  const popovers = useMemo(() => {
    const arr = [];
    for (let i = 0; i < explainerSteps; i++) {
      arr.push(i);
    }
    return arr;
  }, [explainerSteps]);

  const nextBtnCopy =
    activeExplainer === popovers[popovers.length - 1] ? "Finish" : "Next";

  const handleNext = () => {
    if (activeExplainer === popovers[popovers.length - 1]) {
      onFlowEnd && onFlowEnd();
    }
    setActiveExplainer((prev) => {
      if (prev === undefined || prev >= explainerSteps - 1) return;
      else return prev + 1;
    });
  };

  return (
    <div className="custom-popover-content">
      {heading && <h6 className="heading">{heading}</h6>}
      <p className="mb-1">{popoverContent}</p>
      <StepIndicator
        currentStep={activeExplainer === undefined ? 0 : activeExplainer}
        totalSteps={explainerSteps}
      />
      <div className="actions-container">
        {activeExplainer !== popovers[0] && (
          <button className="filled-white" onClick={handlePrevious}>
            Previous
          </button>
        )}
        <button className="filled-primary" onClick={handleNext}>
          {nextBtnCopy}
        </button>
      </div>
    </div>
  );
}

function CustomPopover({
  isOpen,
  activeExplainer,
  setActiveExplainer,
  explainerSteps,
  popoverContent,
  onFlowEnd,
  heading,
  children,
  positions = "bottom",
  childrenContainerStyles,
}: PropsWithChildren<CustomPopoverProps>) {
  return (
    <>
      <Popover
        isOpen={isOpen}
        positions={positions}
        containerStyle={{ zIndex: "999" }}
        content={({ position, childRect, popoverRect }) => (
          <ArrowContainer
            position={position}
            childRect={childRect}
            popoverRect={popoverRect}
            arrowColor="#F4F0FF"
            arrowSize={10}
            className="popover-arrow-container"
            arrowClassName="popover-arrow"
          >
            <PopoverContent
              activeExplainer={activeExplainer}
              setActiveExplainer={setActiveExplainer}
              popoverContent={popoverContent}
              explainerSteps={explainerSteps}
              heading={heading}
              onFlowEnd={onFlowEnd}
            />
          </ArrowContainer>
        )}
      >
        <div className="popover-node-wrapper" style={childrenContainerStyles}>
          {children}
        </div>
      </Popover>
      {isOpen && <Overlay />}
    </>
  );
}

export default CustomPopover;
